import { addDays } from 'date-fns';
import { showErrorMessage } from '@acorn-portal/shared/src/components/toast/messageUtils';
import { IMidTermAdjustmentRatingResponseState } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { PaymentType } from '@acorn-portal/shared/src/utils/types/payment';
import { MtaPaymentCollectionOption } from '@acorn-portal/shared/src/services/api';
/**
 * Checks form conditions for MTA changes and determines next actions
 */
export const checkFormConditions = (
	formHasChanged: any,
	formMTAChanges: any,
	mtaResponse: IMidTermAdjustmentRatingResponseState,
	changesDetected: boolean,
	alternateChangeDetected?: boolean,
): {
	updateSection: boolean;
	continueToRate: boolean;
	navigateToSummary: boolean;
} => {
	//Checks Initial MTA Change - prior to hitting results summary screen
	if (!formHasChanged && !mtaResponse.underwritingOutcomeResponseId && !alternateChangeDetected) {
		showErrorMessage('You are unable to proceed, as you did not make any changes to your policy.');
		return {
			updateSection: false,
			continueToRate: false,
			navigateToSummary: false,
		};
	}
	//Checks Has Current MTA and If user has revered back to original policy detail for this section
	else if (mtaResponse.underwritingOutcomeResponseId && !formHasChanged && !alternateChangeDetected) {
		if (!changesDetected) {
			return {
				updateSection: false,
				continueToRate: false,
				navigateToSummary: true,
			};
		}
		return {
			updateSection: false,
			continueToRate: true,
			navigateToSummary: true,
		};
	}
	//Checks Has Current MTA and no changes have been made while on the form
	else if (!formMTAChanges && mtaResponse.underwritingOutcomeResponseId && !alternateChangeDetected) {
		return {
			updateSection: true,
			continueToRate: false,
			navigateToSummary: true,
		};
	}
	//Checks if changes have been made while on another page and needs to rerate the policy regardless
	else if (!formMTAChanges && !mtaResponse.underwritingOutcomeResponseId && alternateChangeDetected) {
		return {
			updateSection: false,
			continueToRate: true,
			navigateToSummary: true,
		};
	}
	//Checks changes have been made while on the form for new MTA
	return { updateSection: true, continueToRate: true, navigateToSummary: true };
};

/**
 * Returns a date with only date components (no time)
 */
export const getDateOnly = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Gets the date range for MTA start dates (today to 30 days from now)
 */
export const GetMtaStartDateRange = (): [Date, Date] => {
	const min = new Date();
	const max = addDays(new Date(), 30);
	return [min, max];
};

/**
 * Finds the first payment option from the provided list that matches one of the specified payment types.
 * The payment options are sorted in descending order based on the applied installment count before searching.
 */
export const findPaymentOptionHasOneOfTypes = (
	paymentOptions: MtaPaymentCollectionOption[],
	paymentTypes: PaymentType[],
): MtaPaymentCollectionOption | undefined => {
	const sortedPaymentOption = [...paymentOptions];
	return sortedPaymentOption
		.sort((a, b) => (b.appliedInstalmentCount ?? 0) - (a.appliedInstalmentCount ?? 0))
		.find((x) => x.code.split('|').some((type) => paymentTypes.some((enumVal) => enumVal === type)));
};

/**
 * Checks if there is any payment option in the provided list that matches one of the specified payment types.
 */
export const hasPaymentOptionOfType = (
	paymentOptions: MtaPaymentCollectionOption[],
	paymentType: PaymentType[],
): boolean => {
	const paymentOption = findPaymentOptionHasOneOfTypes(paymentOptions, paymentType);
	return paymentOption !== undefined;
};
